import { useContext, useEffect, useState } from "react";
import Portal from "../Portal/Portal";

import UpdatedAssetsList from "./UpdatedAssetsList";
import { IUpdateAssetsListResponse } from "../../models/IUpdateAssetsListResponse";
import genericRepositoryService from "../../services/genericRepositoryService";

import styles from "./assetUpdateModal.module.css";
import AssetReplacePreview from "./AssetReplacePreview";
import IAsset from "../../models/IAsset";
import { ASSET_TYPE_ID } from "../../const";
import { AssetContext, ILessonPageActionsContext, LessonPageActionsContext } from "../../routes/builderContexts";
import { useConfirmation } from "../../contexts/Confirmation";

const title = "Asset Update";
const successMessage = "The assets have been updated successfully.";
const message =
  "The following assets have been updated and will need to be replaced in order to have the latest asset version in the lesson.";

interface AssetUpdateModalProps {
  open: boolean;
  onClose: () => void;
  lessonVersionId?: number;
}

export default function AssetUpdateModal({ open, onClose, lessonVersionId }: Readonly<AssetUpdateModalProps>) {
  const lessonPageActions = useContext<ILessonPageActionsContext>(LessonPageActionsContext);
  const [updatedAssetsList, setUpdatedAssetsList] = useState<IUpdateAssetsListResponse | null>();
  const [selectedAssetList, setSelectedAssetList] = useState<boolean[] | null>();
  const [isLoading, setIsLoading] = useState(true);
  const [replacePhase, setReplacePhase] = useState(false);
  const { confirm: confirmSuccessMessage } = useConfirmation();
  const [currentIndexToReplace, setCurrentIndexToReplace] = useState<number | null>();
  const { setAllPageAssetsUseActiveVersion } = useContext(AssetContext);

  useEffect(() => {
    const handleFetchAssets = async (lessonVersionId: number) => {
      const response = await genericRepositoryService.getUpdatedAssetsList(lessonVersionId);

      if (response.errorMessage) {
        setUpdatedAssetsList(null);

        return;
      }

      setUpdatedAssetsList(response.data);
      setSelectedAssetList(response.data.items.map(() => false));
      setIsLoading(false);
    };

    if (open && !updatedAssetsList && lessonVersionId) {
      handleFetchAssets(lessonVersionId);
    }
  }, [open]);

  useEffect(() => {
    if (!replacePhase && updatedAssetsList?.items.length === 0) {
      confirmSuccessMessage({
        title: "Asset Update",
        message: successMessage,
        type: "success",
      }).then((result) => {
        if (result) {
          onClose();
          setAllPageAssetsUseActiveVersion(true);
        }
      });
    }
  }, [updatedAssetsList?.items.length, replacePhase]);

  if (!open) {
    return null;
  }

  const handleSelectAsset = (index: number) => {
    setSelectedAssetList((prevSelectedAssetList) => {
      const newSelectedAssetList = prevSelectedAssetList ? [...prevSelectedAssetList] : [];

      newSelectedAssetList[index] = !newSelectedAssetList[index];
      return newSelectedAssetList;
    });
  };

  function getAssetPath(assetIndex: number) {
    const sourceAsset = updatedAssetsList?.items[assetIndex].sourceAsset;
    const newAsset = updatedAssetsList?.items[assetIndex].newAsset;
    const pagesAffected = updatedAssetsList?.items[assetIndex].pagesAffected;

    return {
      sourceAsset,
      newAsset,
      pagesAffected,
    };
  }

  // Helpers for navigation based on the current selection list
  const getNextSelectedIndex = (fromIndex: number | null | undefined) => {
    if (!selectedAssetList || fromIndex == null) return -1;
    for (let i = fromIndex + 1; i < selectedAssetList.length; i++) {
      if (selectedAssetList[i]) return i;
    }
    return -1;
  };
  const getPrevSelectedIndex = (fromIndex: number | null | undefined) => {
    if (!selectedAssetList || fromIndex == null) return -1;
    for (let i = (fromIndex ?? 0) - 1; i >= 0; i--) {
      if (selectedAssetList[i]) return i;
    }
    return -1;
  };

  if (replacePhase) {
    const { sourceAsset, newAsset } = getAssetPath(currentIndexToReplace ?? 0);

    const navigateAfterRemoval = (prevSelection: boolean[], removedIndex: number): number => {
      // Look for the next selected item to the right in the previous state
      let nextIdx = -1;
      for (let i = removedIndex + 1; i < prevSelection.length; i++) {
        if (prevSelection[i]) {
          nextIdx = i - 1; // After removal, elements to the right shift by -1
          break;
        }
      }
      if (nextIdx !== -1) return nextIdx;
      // If none on the right, find the last selected item to the left
      for (let i = removedIndex - 1; i >= 0; i--) {
        if (prevSelection[i]) {
          return i; // Indices on the left remain the same
        }
      }
      return -1;
    };

    const navigateAfterUnselect = (prevSelection: boolean[], unselectedIndex: number): number => {
      // Prioritize finding the next item to the right
      for (let i = unselectedIndex + 1; i < prevSelection.length; i++) {
        if (prevSelection[i]) return i;
      }
      // If none to the right, fallback to finding any selected item to the left
      for (let i = unselectedIndex - 1; i >= 0; i--) {
        if (prevSelection[i]) return i;
      }
      return -1;
    };

    const handleDrop = () => {
      const idx = currentIndexToReplace ?? -1;
      if (idx < 0) return;

      // Compute next index based on the previous selection state (no removal)
      const prevSelection = selectedAssetList ? [...selectedAssetList] : [];
      const targetIndex = navigateAfterUnselect(prevSelection, idx);

      setSelectedAssetList((prevSel) => (prevSel ? prevSel.map((v, i) => (i === idx ? false : v)) : prevSel));

      if (targetIndex !== -1) {
        setCurrentIndexToReplace(targetIndex);
      } else {
        setReplacePhase(false);
        setCurrentIndexToReplace(null);
      }
    };

    const handleApply = () => {
      const idx = currentIndexToReplace ?? -1;
      if (idx < 0) return;
      const { sourceAsset, newAsset, pagesAffected } = getAssetPath(idx);

      if (sourceAsset && newAsset && pagesAffected) {
        handleUpdateAsset(sourceAsset, newAsset, pagesAffected);
      }

      // Compute next index based on the previous selection state (before removal)
      const prevSelection = selectedAssetList ? [...selectedAssetList] : [];
      const targetIndex = navigateAfterRemoval(prevSelection, idx);

      setSelectedAssetList((prevSel) => (prevSel ? prevSel.filter((_, i) => i !== idx) : prevSel));
      setUpdatedAssetsList((prev) => {
        if (!prev) return prev;
        const items = prev.items.filter((_, i) => i !== idx);
        return { ...prev, items };
      });

      if (targetIndex !== -1) {
        setCurrentIndexToReplace(targetIndex);
      } else {
        setReplacePhase(false);
        setCurrentIndexToReplace(null);
      }
    };

    const handleUpdateImage = (sourceAsset: IAsset, newAsset: IAsset, pagesAffected: number[]) => {
      lessonPageActions.updateLessonAsset(sourceAsset, newAsset, pagesAffected);
    };

    const handleUpdateSmartObject = (sourceAsset: IAsset, newAsset: IAsset, pagesAffected: number[]) => {
      lessonPageActions.updateLessonAsset(sourceAsset, newAsset, pagesAffected);
    };

    const handleUpdateAsset = (sourceAsset: IAsset, newAsset: IAsset, pagesAffected: number[]) => {
      switch (newAsset.assetTypeId) {
        case ASSET_TYPE_ID.IMAGE:
          handleUpdateImage(sourceAsset, newAsset, pagesAffected);
          break;
        case ASSET_TYPE_ID.SMART_OBJECT:
          handleUpdateSmartObject(sourceAsset, newAsset, pagesAffected);
          break;
        default:
          alert("Not implemented yet");
      }
    };

    const nextAssetIndex = getNextSelectedIndex(currentIndexToReplace ?? -1);
    const previousAssetIndex = getPrevSelectedIndex(currentIndexToReplace ?? -1);

    return (
      <Portal>
        <div className={`modalBackdrop ${styles.modalContainer}`}>
          <div className={styles.modalContent}>
            <div aria-label="Asset Update Modal Header">
              <span>{title}</span>
              <button onClick={onClose} />
            </div>
            <div aria-label="Asset Update Modal Body">
              <div aria-label="Asset Update Modal Buttons" className={styles.assetComparisonButtons}>
                <button
                  disabled={previousAssetIndex === -1}
                  onClick={() => {
                    setCurrentIndexToReplace(previousAssetIndex);
                  }}
                >
                  Previous
                </button>
                <button
                  disabled={nextAssetIndex === -1}
                  onClick={() => {
                    setCurrentIndexToReplace(nextAssetIndex);
                  }}
                >
                  Next
                </button>
              </div>
              <div className={styles.assetComparison}>
                <AssetReplacePreview sourceAsset={sourceAsset} newAsset={newAsset} key={currentIndexToReplace} />
              </div>
            </div>
            <div aria-label="Asset Update Modal Footer">
              <button onClick={onClose}>Close</button>
              <button
                onClick={() => {
                  setReplacePhase(false);
                  setCurrentIndexToReplace(null);
                }}
              >
                Return to asset list
              </button>
              <button onClick={handleDrop}>Drop</button>
              <button onClick={handleApply}>Apply</button>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  const hasSelectedAssets = selectedAssetList?.some((asset) => asset);

  return (
    <Portal>
      <div className={`modalBackdrop ${styles.modalContainer}`}>
        <div className={styles.modalContent}>
          <div aria-label="Asset Update Modal Header">
            <span>{title}</span>
            <button onClick={onClose} />
          </div>
          <div aria-label="Asset Update Modal Body">
            <p>{message}</p>
            {isLoading ? (
              <p>Loading...</p>
            ) : (
              <UpdatedAssetsList
                assetsList={updatedAssetsList?.items}
                selectedAssetList={selectedAssetList}
                handleSelectAsset={handleSelectAsset}
              />
            )}
          </div>
          <div aria-label="Asset Update Modal Footer">
            <button onClick={onClose}>Close</button>
            <button
              onClick={() => {
                setReplacePhase(true);
                setCurrentIndexToReplace(selectedAssetList?.findIndex((asset) => asset) ?? null);
              }}
              disabled={isLoading || !hasSelectedAssets}
            >
              Replace Assets
            </button>
            <button
              onClick={() => {
                const allSelected: boolean[] = updatedAssetsList ? updatedAssetsList.items.map(() => true) : [];
                setSelectedAssetList(allSelected);
                setCurrentIndexToReplace(0);
                setReplacePhase(true);
              }}
            >
              Replace All
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
